// yup validation for Formik in NewLeasePopUp
import { object, string, number, date, boolean, ref, bool, array } from "yup";
import { DateTime } from "luxon";
import { carFeeType, scooterFeeType } from "../config/config";

function testRentDateInLease(value) {
  const startTime = new Date(this.resolve(ref("rentStartDate"))).getTime();
  const endTime = new Date(this.resolve(ref("rentEndDate"))).getTime();
  const rentTime = DateTime.fromJSDate(value)
    .plus({ hours: 8 })
    .toJSDate()
    .getTime(); // calculate the timezone difference caused by Yup
  return startTime <= rentTime && rentTime <= endTime;
}

const createLeaseSchema = object().shape({
  rentStartDate: string()
    .required("必須填寫租賃開始日期")
    .test("rent-is-later", "租期結束必須比租期開始晚", function (value) {
      try {
        const endDate = this.resolve(ref("rentEndDate"));
        const startDate = this.resolve(ref("rentStartDate"));
        return new Date(startDate).getTime() <= new Date(endDate).getTime();
      } catch (err) {
        console.error(err);
      }
    }),
  rentEndDate: string().required("必須填寫租賃結束日期"),
  renters: array().min(
    1,
    "必須填寫會員，若找不到會員，請確認該會員是否已填寫身分證字號（會員管理功能）",
  ),
  roomId: string().required("必須填寫房間號碼"),
  // SECTION: scooter 1
  haveScooterParkingSpace: boolean().required("必須填寫是否有摩托車停車位"),
  scooterLeaseStartDate: date().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃開始日期")
        // if the first test fail, the second test won't run
        .test(
          "scooter-is-later",
          "機車位租期結束必須比租期開始晚",
          function (value) {
            try {
              const endDate = this.resolve(ref("scooterLeaseEndDate"));
              return new Date(value) < new Date(endDate);
            } catch (err) {
              console.error(err);
              return false;
            }
          },
        )
        .test(
          "in-lease",
          "機車位租期開始必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterLeaseEndDate: date().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃結束日期")
        .test(
          "in-lease",
          "機車位租期結束必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterParkingSpaceId: string().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) => schema.required("當有機車停車位時，必須填寫機車車位"),
  }),
  scooterFee: number().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .typeError(`${scooterFeeType}必須是數字`)
        .integer(`${scooterFeeType}必須是整數`)
        .test(
          "scooterFee greater than 0",
          `${scooterFeeType}必須至少為零`,
          (value) => value >= 0,
        )
        .required(`當有機車停車位時，必須填寫${scooterFeeType}`),
  }),
  // SECTION: scooter 2
  haveScooterParkingSpace2: boolean().required("必須填寫是否有摩托車停車位"),
  scooterLeaseStartDate2: date().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃開始日期")
        // if the first test fail, the second test won't run
        .test(
          "scooter-is-later2",
          "機車位租期結束必須比租期開始晚",
          function (value) {
            try {
              const endDate = this.resolve(ref("scooterLeaseEndDate2"));
              return new Date(value) < new Date(endDate);
            } catch (err) {
              console.error(err);
              return false;
            }
          },
        )
        .test(
          "in-lease",
          "機車位租期開始必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterLeaseEndDate2: date().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃結束日期")
        .test(
          "in-lease",
          "機車位租期結束必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterParkingSpaceId2: string().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) => schema.required("當有機車停車位時，必須填寫機車車位"),
  }),
  scooterFee2: number().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) =>
      schema
        .typeError(`${scooterFeeType}必須是數字`)
        .integer(`${scooterFeeType}必須是整數`)
        .test(
          "scooterFee greater than 0",
          `${scooterFeeType}必須至少為零`,
          (value) => value >= 0,
        )
        .required(`當有機車停車位時，必須填寫${scooterFeeType}`),
  }),
  // SECTION: car
  haveCarParkingSpace: boolean().required("必須填寫是否有汽車停車位"),
  carLeaseStartDate: date().when("haveCarParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有汽車停車位時，必須填寫汽車租賃開始日期")
        .test(
          "car-is-later",
          "汽車位租期結束必須比租期開始晚",
          function (value) {
            try {
              const endDate = this.resolve(ref("carLeaseEndDate"));
              return new Date(value) < new Date(endDate);
            } catch (err) {
              console.error(err);
              return false;
            }
          },
        )
        .test(
          "in-lease",
          "汽車位租期開始必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  carLeaseEndDate: date().when("haveCarParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有汽車停車位時，必須填寫汽車租賃結束日期")
        .test(
          "in-lease",
          "汽車位租期結束必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  carParkingSpaceId: string().when("haveCarParkingSpace", {
    is: true,
    then: (schema) => schema.required("當有汽車停車位時，必須填寫汽車車位"),
  }),
  carFee: number().when("haveCarParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .typeError(`${carFeeType}必須是數字`)
        .integer(`${carFeeType}必須是整數`)
        .test(
          "carFee greater than 0",
          `${carFeeType}必須至少為零`,
          (value) => value >= 0,
        )
        .required(`當有汽車停車位時，必須填寫${carFeeType}`),
  }),
  rentCost: number()
    .typeError("房租必須是數字")
    .integer("房租必須是整數")
    .test("rent >=0", "房租必須至少為零", (value) => value >= 0)
    .required("請填寫房租"),
});

const createFeeSchema = object().shape({
  name: string().required("請填寫費用名稱"),
  deadline: date().typeError("繳費期限必須是日期").required("請填寫繳費期限"),
  cost: number()
    .typeError("金額必須是數字")
    .integer("金額必須是整數")
    .test("cost >=0", "費用必須至少為零", (value) => value >= 0)
    .required("請填寫金額"),
  paid: bool().required("請填寫繳費狀態"),
});

const editFeeSchema = object().shape({
  cost: number()
    .typeError("金額必須是數字")
    .integer("金額必須是整數")
    .test("cost >=0", "金額必須至少為零", (value) => value >= 0)
    .required("請填寫金額"),
  paid: bool().required("請填寫繳費狀態"),
});

const editLeaseSchema = object().shape({
  // SECTION: scooter
  haveScooterParkingSpace: boolean().required("必須填寫是否有摩托車停車位"),
  scooterLeaseStartDate: date().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃開始日期")
        // if the first test fail, the second test won't run
        .test(
          "scooter-is-later",
          "機車位租期結束必須比租期開始晚",
          function (value) {
            try {
              const endDate = this.resolve(ref("scooterLeaseEndDate"));
              return new Date(value) < new Date(endDate);
            } catch (err) {
              console.error(err);
              return false;
            }
          },
        )
        .test(
          "in-lease",
          "機車位租期開始必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterLeaseEndDate: date().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃結束日期")
        .test(
          "in-lease",
          "機車位租期結束必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterParkingSpaceId: string().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) => schema.required("當有機車停車位時，必須填寫機車車位"),
  }),
  scooterFee: number().when("haveScooterParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .typeError(`${scooterFeeType}必須是數字`)
        .integer(`${scooterFeeType}必須是整數`)
        .test(
          "scooterFee greater than 0",
          `${scooterFeeType}必須至少為零`,
          (value) => value >= 0,
        )
        .required(`當有機車停車位時，必須填寫${scooterFeeType}`),
  }),
  // SECTION: scooter2
  haveScooterParkingSpace2: boolean().required("必須填寫是否有摩托車停車位"),
  scooterLeaseStartDate2: date().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃開始日期")
        // if the first test fail, the second test won't run
        .test(
          "scooter-is-later",
          "機車位租期結束必須比租期開始晚",
          function (value) {
            try {
              const endDate = this.resolve(ref("scooterLeaseEndDate2"));
              return new Date(value) < new Date(endDate);
            } catch (err) {
              console.error(err);
              return false;
            }
          },
        )
        .test(
          "in-lease",
          "機車位租期開始必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterLeaseEndDate2: date().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) =>
      schema
        .required("當有機車停車位時，必須填寫機車租賃結束日期")
        .test(
          "in-lease",
          "機車位租期結束必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  scooterParkingSpaceId2: string().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) => schema.required("當有機車停車位時，必須填寫機車車位"),
  }),
  scooterFee2: number().when("haveScooterParkingSpace2", {
    is: true,
    then: (schema) =>
      schema
        .typeError(`${scooterFeeType}必須是數字`)
        .integer(`${scooterFeeType}必須是整數`)
        .test(
          "scooterFee greater than 0",
          `${scooterFeeType}必須至少為零`,
          (value) => value >= 0,
        )
        .required(`當有機車停車位時，必須填寫${scooterFeeType}`),
  }),
  // SECTION: car
  haveCarParkingSpace: boolean().required("必須填寫是否有汽車停車位"),
  carLeaseStartDate: date().when("haveCarParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有汽車停車位時，必須填寫汽車租賃開始日期")
        .test(
          "car-is-later",
          "汽車位租期結束必須比租期開始晚",
          function (value) {
            try {
              const endDate = this.resolve(ref("carLeaseEndDate"));
              return new Date(value) < new Date(endDate);
            } catch (err) {
              console.error(err);
              return false;
            }
          },
        )
        .test(
          "in-lease",
          "汽車位租期開始必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  carLeaseEndDate: date().when("haveCarParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .required("當有汽車停車位時，必須填寫汽車租賃結束日期")
        .test(
          "in-lease",
          "汽車位租期結束必須在租房期間內",
          testRentDateInLease,
        ),
  }),
  carParkingSpaceId: string().when("haveCarParkingSpace", {
    is: true,
    then: (schema) => schema.required("當有汽車停車位時，必須填寫汽車車位"),
  }),
  carFee: number().when("haveCarParkingSpace", {
    is: true,
    then: (schema) =>
      schema
        .typeError(`${carFeeType}必須是數字`)
        .integer(`${carFeeType}必須是整數`)
        .test(
          "carFee greater than 0",
          `${carFeeType}必須至少為零`,
          (value) => value >= 0,
        )
        .required(`當有汽車停車位時，必須填寫${carFeeType}`),
  }),
  // SECTION: rent
  rentStartDate: string()
    .required("必須填寫租賃開始日期")
    .test("rent-is-later", "租期結束必須比租期開始晚", function (value) {
      try {
        const endDate = this.resolve(ref("rentEndDate"));
        const startDate = this.resolve(ref("rentStartDate"));
        return new Date(startDate).getTime() <= new Date(endDate).getTime();
      } catch (err) {
        console.error(err);
      }
    }),
  rentEndDate: string().required("必須填寫租賃結束日期"),
  roomId: string().required("必須填寫房間號碼"),
  rentCost: number()
    .typeError("房租必須是數字")
    .integer("房租必須是整數")
    .test("rent >=0", "房租必須至少為零", (value) => value >= 0)
    .required("請填寫房租"),
});

export { createLeaseSchema, createFeeSchema, editFeeSchema, editLeaseSchema };
